import React from 'react'
import { useFormatMessage } from '@babylon/intl'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material'
import { AlertFilled, TickCircleFilled } from '@babylon/icons'

import messages from '../Chats.messages'
import {
  dialogPaperSx,
  dialogPaperCenteredSx,
  dialogTitleSx,
  dialogBodySx,
  dialogContentSx,
  dialogActionsSx,
  dialogCenteredContentSx,
  dialogStatusTextSx,
  dialogErrorTextSx,
  dialogConfirmButtonSx,
  dialogCancelButtonSx,
  reassignSpinnerSx,
} from '../Chats.styles'

export enum ReassignDialogState {
  CLOSED = 'closed',
  CONFIRM = 'confirm',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
  NO_AVAILABLE_CLINICIANS = 'no_available_clinicians',
}

interface ReassignDialogProps {
  state: ReassignDialogState
  onClose: () => void
  onConfirm: () => void
  reassignedClinicianName?: string
}

const ReassignDialog: React.FC<ReassignDialogProps> = ({
  state,
  onClose,
  onConfirm,
  reassignedClinicianName,
}) => {
  const fm = useFormatMessage()
  const isOpen = state !== ReassignDialogState.CLOSED

  const renderConfirm = () => (
    <>
      <DialogTitle id="reassign-dialog-title" sx={dialogTitleSx}>
        {fm(messages.reassign_dialog_title)}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <DialogContentText id="reassign-dialog-description" sx={dialogBodySx}>
          {fm(messages.reassign_dialog_body)}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} sx={dialogCancelButtonSx}>
          {fm(messages.reassign_dialog_cancel)}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={dialogConfirmButtonSx}
          autoFocus
        >
          {fm(messages.reassign_button)}
        </Button>
      </DialogActions>
    </>
  )

  const renderLoading = () => (
    <DialogContent sx={dialogCenteredContentSx}>
      <CircularProgress size={48} sx={reassignSpinnerSx} />
      <DialogContentText sx={dialogStatusTextSx}>
        {fm(messages.reassign_loading)}
      </DialogContentText>
    </DialogContent>
  )

  const renderSuccess = () => (
    <>
      <DialogContent sx={dialogCenteredContentSx}>
        <TickCircleFilled width={48} fill="#4caf50" />
        <DialogContentText sx={dialogStatusTextSx}>
          {reassignedClinicianName
            ? fm(messages.reassign_success_with_name, {
                clinicianName: reassignedClinicianName,
              })
            : fm(messages.reassign_success)}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={dialogConfirmButtonSx}
          autoFocus
        >
          {fm(messages.reassign_dialog_okay)}
        </Button>
      </DialogActions>
    </>
  )

  const renderError = () => (
    <>
      <DialogContent sx={dialogCenteredContentSx}>
        <AlertFilled width={48} fill="var(--warning-tag-border)" />
        <DialogContentText sx={dialogErrorTextSx}>
          {fm(messages.reassign_error)}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={dialogConfirmButtonSx}
          autoFocus
        >
          {fm(messages.reassign_dialog_okay)}
        </Button>
      </DialogActions>
    </>
  )

  const renderNoAvailableClinicians = () => (
    <>
      <DialogContent sx={dialogCenteredContentSx}>
        <AlertFilled width={48} fill="var(--warning-tag-border)" />
        <DialogContentText sx={dialogErrorTextSx}>
          {fm(messages.reassign_no_available_clinicians)}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={dialogConfirmButtonSx}
          autoFocus
        >
          {fm(messages.reassign_dialog_okay)}
        </Button>
      </DialogActions>
    </>
  )

  const renderContent = () => {
    switch (state) {
      case ReassignDialogState.CONFIRM:
        return renderConfirm()
      case ReassignDialogState.LOADING:
        return renderLoading()
      case ReassignDialogState.SUCCESS:
        return renderSuccess()
      case ReassignDialogState.ERROR:
        return renderError()
      case ReassignDialogState.NO_AVAILABLE_CLINICIANS:
        return renderNoAvailableClinicians()
      default:
        return null
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={state === ReassignDialogState.CONFIRM ? onClose : undefined}
      aria-labelledby="reassign-dialog-title"
      aria-describedby="reassign-dialog-description"
      PaperProps={{
        sx:
          state === ReassignDialogState.CONFIRM
            ? dialogPaperSx
            : dialogPaperCenteredSx,
      }}
    >
      {renderContent()}
    </Dialog>
  )
}

export default ReassignDialog
